"use client";

import { useCallback, useState } from "react";

import { ConfirmationDialog } from "../dialog/dialog";
import { Section } from "../accordion/accordion";

import styles from "./account-section.module.scss";

export interface AccountSectionUser {
  name: string | null;
  email: string;
  avatarUrl: string | null;
}

export interface AccountSectionProps {
  user: AccountSectionUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  onSignIn: () => void;
  onSignOut: () => Promise<void>;
  onDeleteAccount: () => Promise<void>;
  signInSubtitle?: string;
}

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

export function AccountSection({
  user,
  isAuthenticated,
  isLoading,
  onSignIn,
  onSignOut,
  onDeleteAccount,
  signInSubtitle = "Sign in to prepare for cross-device sync",
}: AccountSectionProps) {
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);

  const handleSignOut = useCallback(async () => {
    await onSignOut();
    setShowSignOutConfirm(false);
  }, [onSignOut]);

  const handleDeleteAccount = useCallback(async () => {
    try {
      setDeleteError(null);
      await onDeleteAccount();
      setShowDeleteConfirm(false);
    } catch {
      setDeleteError("Failed to delete account. Please try again.");
    }
  }, [onDeleteAccount]);

  if (isLoading) return null;

  if (!isAuthenticated || !user) {
    return (
      <Section title="Account">
        <div className={styles.signInPrompt}>
          <button
            type="button"
            className={styles.googleButton}
            onClick={onSignIn}
            data-testid="sign-in-button"
          >
            <GoogleIcon />
            Sign in with Google
          </button>
          <p className={styles.subtitle}>{signInSubtitle}</p>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Account">
      <div className={styles.userCard} data-testid="user-card">
        <div className={styles.avatar}>
          {user.avatarUrl && !avatarError ? (
            <img
              src={user.avatarUrl}
              alt=""
              crossOrigin="anonymous"
              className={styles.avatarImage}
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span className={styles.initials}>{getInitials(user.name)}</span>
          )}
        </div>
        <div className={styles.userInfo}>
          {user.name && <span className={styles.userName}>{user.name}</span>}
          <span className={styles.userEmail}>{user.email}</span>
        </div>
      </div>
      <button
        type="button"
        className={styles.signOutButton}
        onClick={() => setShowSignOutConfirm(true)}
        data-testid="sign-out-button"
      >
        Sign out
      </button>
      <button
        type="button"
        className={styles.deleteAccountButton}
        onClick={() => setShowDeleteConfirm(true)}
        data-testid="delete-account-button"
      >
        Delete Account
      </button>
      {deleteError && <p className={styles.deleteError}>{deleteError}</p>}

      {showSignOutConfirm && (
        <ConfirmationDialog
          title="Sign Out"
          message="Your local data will be kept. You can sign in again anytime."
          confirmLabel="Sign Out"
          onConfirm={() => void handleSignOut()}
          onCancel={() => setShowSignOutConfirm(false)}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationDialog
          title="Delete Account"
          message="This will permanently delete your account and all synced data. Local data will also be cleared. This cannot be undone."
          confirmLabel="Delete Account"
          destructive
          onConfirm={() => void handleDeleteAccount()}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}
    </Section>
  );
}

function GoogleIcon() {
  return (
    <svg className={styles.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
